import { useState } from "react";
import { ExclamationTriangleIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";
import {
    createTable,
    NO_SORTING_STATE,
    PaginationState,
    SearchState,
    SortingState,
    TableHeaderSpec,
} from "../../../../../components/table/CreateTable.tsx";
import { MoneyLabel } from "../../../../../components/general/MoneyLabel.tsx";
import { toDecimalFromMoney } from "../../../../../lib/Money.ts";
import type { SalesInvoiceRow } from "./types/salesInvoice";
import { formatDateDDMMYYYY, statusSortIndex } from "./utils/salesInvoicesFormat";
import { SalesInvoiceStatusPill } from "./SalesInvoiceStatusPill.tsx";

function DeliveryIndicator({ row }: { row: SalesInvoiceRow }) {
    if (row.email_bounced) {
        return (
            <span title="De e-mail kon niet worden bezorgd">
                <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />
            </span>
        );
    }
    if (row.last_sent_at) {
        return (
            <span title={`Verstuurd per e-mail op ${formatDateDDMMYYYY(row.last_sent_at)}`}>
                <PaperAirplaneIcon className="h-4 w-4 text-emerald-600" />
            </span>
        );
    }
    return null;
}

interface Props {
    invoices: SalesInvoiceRow[];
    isLoading: boolean;
    emptyMessage: string;
    onRowClick: (invoice: SalesInvoiceRow) => void;
}

const PAGE_SIZE = 25;
const SKELETON_ROWS = 6;

const MISSING_VALUE_LABEL = "—";
const EMPTY_FIELD_VALUE = "";

const SEARCHABLE_FIELDS: Array<(row: SalesInvoiceRow) => string | null> = [
    (row) => row.invoice_number,
    (row) => row.customer_name,
    (row) => row.description,
    (row) => row.file_name,
];

export function SalesInvoicesTable({ invoices, isLoading, emptyMessage, onRowClick }: Props) {
    const tablePaginationState = useState<PaginationState<SalesInvoiceRow>>();
    const tableSortingState = useState<SortingState>(NO_SORTING_STATE);

    const tableSpec: TableHeaderSpec<SalesInvoiceRow> = {
        isLoading,
        skeletonRows: SKELETON_ROWS,
        columns: [
            {
                name: "Factuur",
                field: "invoice_number",
                customRender: (row) => (
                    <div>
                        <div className="flex items-center gap-1.5 font-medium text-gray-900">
                            {row.invoice_number ?? MISSING_VALUE_LABEL}
                            <DeliveryIndicator row={row} />
                        </div>
                        <div className="mt-0.5 text-xs text-gray-500">
                            {formatDateDDMMYYYY(row.invoice_date)}
                        </div>
                    </div>
                ),
                style: { widthMultiplier: 1.5, skeleton: "lead" },
            },
            {
                name: "Klant",
                field: "customer_name",
                customRender: (row) => (
                    <div className="min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                            {row.customer_name || MISSING_VALUE_LABEL}
                        </div>
                        {row.description && (
                            <div className="mt-0.5 text-xs text-gray-500 truncate">
                                {row.description}
                            </div>
                        )}
                    </div>
                ),
                style: { widthMultiplier: 2.5, skeleton: "lead" },
            },
            {
                name: "Bedrag",
                field: "amount",
                customRender: (row) => (
                    <span className="font-medium text-gray-900">
                        <MoneyLabel
                            value={toDecimalFromMoney(row.amount)}
                            showCurrency={true}
                            currency={row.amount.currency}
                        />
                    </span>
                ),
                sortValue: (row) => row.amount.signed_amount,
                style: { widthMultiplier: 1 },
            },
            {
                name: "Vervaldatum",
                field: "due_date",
                customRender: (row) => (
                    <span className="text-gray-700">{formatDateDDMMYYYY(row.due_date)}</span>
                ),
                style: { widthMultiplier: 1 },
            },
            {
                name: "Status",
                field: "status",
                customRender: (row) => <SalesInvoiceStatusPill status={row.status} />,
                sortValue: (row) => statusSortIndex(row.status),
                style: { widthMultiplier: 1 },
            },
        ],
        onEmptyRender: () => <span className="text-gray-500">{emptyMessage}</span>,
        pagination: {
            inMemory: true,
            paginationState: tablePaginationState,
            totalPages: Math.ceil(invoices.length / PAGE_SIZE),
            defaultPageSize: PAGE_SIZE,
        },
        sorting: {
            sortingState: tableSortingState,
            inMemory: true,
        },
        search: {
            searchFn: (row: SalesInvoiceRow, searchState: SearchState) => {
                const query = searchState.currentQuery.toLowerCase();
                return SEARCHABLE_FIELDS.some((readField) =>
                    (readField(row) ?? EMPTY_FIELD_VALUE).toLowerCase().includes(query)
                );
            },
        },
        onRowClick,
        options: {
            disableTopPaginationWidget: true,
            disableBottomPaginationWidget: false,
            allowFullRowClick: true,
        },
    };
    const TableComponent = createTable<SalesInvoiceRow>(invoices, tableSpec);
    return <>{TableComponent()}</>;
}
